import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const Analyses = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchReports = async () => {
    try {
      const res = await axios.get('https://hasanhaji.com/api/reports/me');
      setReports(res.data.data.reports);
      setLoading(false);
    } catch (err) {
      setError(t('analyses.errorLoading'));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);



  const getReportUrl = async (report) => {
    const backendUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : window.location.origin;

    if (report.shareToken) {
      return `${backendUrl}/api/reports/shared/${report.shareToken}`;
    }


    try {
      const res = await axios.post(`https://hasanhaji.com/api/reports/me/${report.id}/share`);
      const fullUrl = res.data.data.shareUrl;

      const token = fullUrl.split('/').pop();
      return `${backendUrl}/api/reports/shared/${token}`;
    } catch (err) {
      console.error('Error creating share link:', err);


      return null;
    }
  };

  const handleView = async (report) => {
    try {
      // Get or create share token
      let token = report.shareToken;

      if (!token) {
        const res = await axios.post(`https://hasanhaji.com/api/reports/me/${report.id}/share`);
        const fullUrl = res.data.data.shareUrl;
        token = fullUrl.split('/').pop();
      }

      // Navigate to SharedReport page
      navigate(`/shared-report/${token}`);
    } catch (err) {
      console.error('Error viewing report:', err);
      setError(t('analyses.errorOpening'));
    }
  };

  const handleDownload = async (report) => {
    try {
      const url = await getReportUrl(report);
      if (!url) {
        throw new Error('Could not generate URL');
      }

      const response = await fetch(`${url}?download=1`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = downloadUrl;
      link.download = report.originalName || `${report.title || 'report'}.pdf`;

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);

    } catch (err) {
      console.error('Download error:', err);
      setError(t('analyses.errorDownloading'));
    }
  };

  const handleShare = async (report) => {
    try {
      const url = await getReportUrl(report);
      if (!url) return;

      if (navigator.share) {
        try {
          await navigator.share({
            title: report.title,
            text: t('analyses.shareTitle', { title: report.title }),
            url: url,
          });
          return;
        } catch (_) {

        }
      }

      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(url);
          alert(t('analyses.shareCopied'));
          return;
        } catch (_) {
        }
      }

      prompt(t('analyses.shareCopied') + ":\n", url);

    } catch (_) {
    }
  };


  return (
    <div className="container-fluid py-5 bg-light" style={{ minHeight: '100vh' }}>
      <div className="container">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5 gap-3">
          <div>
            <h2 className="fw-bold text-primary mb-1">
              <i className="fas fa-notes-medical me-2"></i> {t('analyses.title')}
            </h2>
            <p className="text-muted mb-0">{t('analyses.subtitle', { name: user?.name })}</p>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger shadow-sm border-0 rounded-3 mb-4" role="alert">
            <i className="fas fa-exclamation-circle me-2"></i> {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-3">{t('analyses.loading')}</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-5">
            <div className="bg-white p-5 rounded-4 shadow-sm d-inline-block">
              <i className="fas fa-file-medical-alt fa-4x text-muted mb-3 opacity-50"></i>
              <h4 className="text-dark fw-bold">{t('analyses.noAnalyses')}</h4>
              <p className="text-muted">{t('analyses.noAnalysesDesc')}</p>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {reports.map((report) => (
              <div key={report.id} className="col-12 col-md-6 col-lg-4 col-xl-3">
                <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden hover-card transition-all">
                  <div className="card-body p-4 d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-4">
                      <div className="bg-danger bg-opacity-10 text-danger p-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                        <i className="fas fa-file-pdf fa-2x"></i>
                      </div>
                      <span className="badge bg-light text-muted border rounded-pill px-3 py-2">
                        {(report.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>

                    <h5 className="card-title fw-bold text-dark mb-2 text-truncate" title={report.title}>
                      {report.title}
                    </h5>

                    <div className="mb-4">
                      <p className="card-text text-muted small mb-0 text-center">
                        <i className="far fa-calendar-alt me-2 text-primary opacity-75"></i>
                        {report.analysisDate
                          ? new Date(report.analysisDate).toLocaleDateString(i18n.language === 'ar' ? 'ar-BH' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                          : new Date(report.createdAt).toLocaleDateString(i18n.language === 'ar' ? 'ar-BH' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>

                    <div className="mt-auto pt-3 border-top d-flex justify-content-center gap-3">
                      <button
                        onClick={() => handleView(report)}
                        className="btn btn-light rounded-circle p-0 d-flex align-items-center justify-content-center hover-scale"
                        style={{ width: '45px', height: '45px', transition: 'transform 0.2s' }}
                        title={t('analyses.view')}
                      >
                        <i className="fas fa-eye text-primary fa-lg"></i>
                      </button>

                      <button
                        onClick={() => handleDownload(report)}
                        className="btn btn-light rounded-circle p-0 d-flex align-items-center justify-content-center hover-scale"
                        style={{ width: '45px', height: '45px', transition: 'transform 0.2s' }}
                        title={t('analyses.download')}
                      >
                        <i className="fas fa-download text-success fa-lg"></i>
                      </button>

                      <button
                        className="btn btn-light rounded-circle p-0 d-flex align-items-center justify-content-center hover-scale"
                        style={{ width: '45px', height: '45px', transition: 'transform 0.2s' }}
                        onClick={() => handleShare(report)}
                        title={t('analyses.share')}
                      >
                        <i className="fas fa-share-alt text-dark fa-lg"></i>
                      </button>

                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        .hover-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
        }
        .hover-scale:hover {
            transform: scale(1.1);
        }
        .transition-all {
            transition: all 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default Analyses;
